import {
  HttpClient,
  HttpEventType,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { computed, inject, Injectable, resource, signal } from '@angular/core';
import { finalize, lastValueFrom, map, Observable } from 'rxjs';
import { SyncResponse } from '../../models/product.models';
import { LogsService } from '../logs/logs.service';
import { RegionService } from '../region/region.service';

export interface ProductFileImportParams {
  file?: File;
  entityCode: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  http = inject(HttpClient);
  logs = inject(LogsService);
  regions = inject(RegionService);

  dataSyncHistory = resource({
    loader: () => lastValueFrom(this.logs.getLog$('ProductSync')),
  });

  noDataSyncHistory = computed(() => this.dataSyncHistory.status() === 'error');

  dataImportHistory = resource({
    params: () => this.regions.selectedRegionId(),
    loader: ({ params }) =>
      lastValueFrom(this.logs.getLog$('ProductImport', params)),
  });

  noDataImportHistory = computed(
    () => this.dataImportHistory.status() === 'error',
  );

  downloadProducts(): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `Product/download?regionId=${this.regions.selectedRegionId()}`,
      {
        responseType: 'blob',
        observe: 'response',
      },
    );
  }

  productCatalogFileName = computed(
    () =>
      `ATB_Product_Catalog_${this.regions.selectedRegionCode()}_${new Date().toISOString().split('T')[0]}`,
  );

  syncProductForUser() {
    return this.http.post<SyncResponse>('Product/sync', {});
  }

  productFileUploading = signal<boolean>(false);
  productFileUploadError = signal<boolean>(false);
  productFileUploadProgress = signal<number | undefined>(undefined);

  uploadProductFile = (file: File, entityCode: string) => {
    this.productFileUploading.set(true);
    const formData = new FormData();
    formData.append('file', file, file.name);

    const url = `genericdataimport/uploadfile?dataType=Product&entityCode=${entityCode}&regionId=${this.regions.selectedRegionId()}`;

    const queryParams = new HttpParams();
    queryParams.set('dataType', 'product');
    queryParams.set('regionId', String(this.regions.selectedRegionId()));

    return this.http
      .post(url, formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event) => {
          // Handle download progress as error
          if (event.type === HttpEventType.DownloadProgress) {
            this.productFileUploadError.set(true);
          }

          // The progress indicator is handled the same for uploads and downloads
          if (
            event.type === HttpEventType.UploadProgress ||
            event.type === HttpEventType.DownloadProgress
          ) {
            const progress = Math.round(
              (100 * event.loaded) / (event.total || 1),
            );
            this.productFileUploadProgress.set(progress);
            return progress;
          } else {
            return 0;
          }
        }),
        finalize(() => {
          this.productFileUploading.set(false);
          this.productFileUploadError.set(false);
        }),
      );
  };
}
